import { DatedPatientProgram, PatientProgramEditor } from "@domain/program/patient-program";
import { PatientProgramsManager } from "@domain/program/patient-programs-manager";
import { ProgramContent, ProgramMetadata } from "@domain/program/program";
import { ProgramManager } from "@domain/program/program-manager";
import { ProgramStatus } from "@domain/program/program-status/program-status";
import { TemplateManager } from "@domain/template/template-manager";
import { isNil } from "@utils/assert";
import { now } from "@utils/date-utils";

export class ProgramOperationsManager {
	constructor(
		private readonly programManager: ProgramManager,
		private readonly patientProgramsManager: PatientProgramsManager,
		private readonly templateManager: TemplateManager,
	) {}

	async performStopProgramOperation(
		patientId: string,
		programId: string,
		editor: PatientProgramEditor,
	): Promise<ProgramMetadata> {
		const result = await this.programManager.stopProgram(patientId, programId);
		this.patientProgramsManager.stopProgramLocally(patientId, result, editor);
		return result;
	}

	async performDeleteOperation(patientId: string, programId: string): Promise<void> {
		await this.programManager.deleteProgram(patientId, programId);
		this.patientProgramsManager.deleteProgramLocally(patientId, programId);
	}

	async performStartOperation(
		patientId: string,
		programContent: ProgramContent,
		status: ProgramStatus,
		editor: PatientProgramEditor,
		programId?: string,
	): Promise<DatedPatientProgram> {
		if (isNil(programContent.startDate) || isNil(programContent.endDate)) {
			throw new Error("performStartOperation called with undefined programDates");
		}

		const isDraft = status === ProgramStatus.Draft;
		const programMetadata = !programId
			? await this.programManager.startDraftProgram(patientId, programContent)
			: isDraft
				? await this.programManager.startDraftProgram(patientId, programContent, programId)
				: await this.programManager.startFutureProgram(patientId, programId, programContent);

		const startedPatientProgram: DatedPatientProgram = {
			id: programMetadata.id,
			name: programContent.name,
			exercisesPerWeek: programContent.days.exercisesPerWeek,
			sessionsPerWeek: programContent.days.sessionsPerWeek,
			startDate: programContent.startDate,
			lastEditionMetadata: {
				date: now(),
				editor,
			},
			endDate: programContent.endDate,
		};
		this.patientProgramsManager.startProgramLocally(patientId, startedPatientProgram);
		return startedPatientProgram;
	}

	async performUpdateOngoingOperation(
		patientId: string,
		programId: string,
		programUpdateData: ProgramContent,
		editor: PatientProgramEditor,
		forceChanges: boolean,
	): Promise<DatedPatientProgram> {
		const programMetadata = await this.programManager.updateOngoingProgram(
			patientId,
			programId,
			programUpdateData,
			forceChanges,
		);

		const startedPatientProgram: DatedPatientProgram = {
			id: programMetadata.id,
			name: programUpdateData.name,
			exercisesPerWeek: programUpdateData.days.exercisesPerWeek,
			sessionsPerWeek: programUpdateData.days.sessionsPerWeek,
			startDate: programMetadata.startDate,
			lastEditionMetadata: {
				date: now(),
				editor,
			},
			endDate: programMetadata.endDate,
		};
		this.patientProgramsManager.startProgramLocally(patientId, startedPatientProgram);
		return startedPatientProgram;
	}

	async performDuplicateAsDraftOperation(patientId: string, programId: string): Promise<string> {
		return await this.patientProgramsManager.duplicateProgramAsDraft(patientId, programId);
	}

	async performTransformToTemplateOperation(
		patientId: string,
		programId: string,
		templateName: string,
	): Promise<string> {
		return await this.templateManager.transformProgramToTemplate(patientId, programId, templateName);
	}
}
